import create from 'zustand';
import produce from 'immer';
import { UseLivePriceRes } from 'global/Datafeed/UseLivePrice/useLivePrice.types';
import { UseOrderbookRes } from 'global/Datafeed/UseOrderbook/useOrderbook.types';
import { transformNewOrderbookListToExisting } from 'features/orderbook/utils';
import { getOrderbookCompanies } from '../api/orderbook';
import { OrderbookCompaniesResponse } from '../types/api/orderbook';

interface State {
  data?: Partial<OrderbookCompaniesResponse>;
  isLoading: boolean;
}

interface Action {
  loadOrderbookCompanies: (
    symbol: string,
  ) => Promise<OrderbookCompaniesResponse>;
  resetOrderbookCompanies: () => void;
  setBidOffer: (bidOffer: UseOrderbookRes) => void;
  setHeader: (livePrice: UseLivePriceRes) => void;
  setIepIev: (iepIev: DatafeedIEPIEVResponse | null) => void;
  setBestBidOffer: (bestBidOffer: BestBidOfferResponse) => void;
}

const initialState: State = {
  data: {},
  isLoading: false,
};

const useOrderbookCompanies = create<State & Action>((set) => ({
  ...initialState,
  loadOrderbookCompanies: async (symbol) => {
    try {
      set({ isLoading: true });

      const axiosResponse = await getOrderbookCompanies(symbol);
      const { data } = axiosResponse.data;
      const transformedData = transformNewOrderbookListToExisting(data);

      set({ data: transformedData, isLoading: false });

      return transformedData;
    } catch (error) {
      set({ isLoading: false });
      return Promise.reject(error);
    }
  },
  resetOrderbookCompanies: () => {
    set({ data: {} });
  },
  setBidOffer: ({ bid, offer }) => {
    set(
      produce<State>((state) => {
        if (bid) {
          state.data.bid = { ...state.data?.bid, ...bid };
        }
        if (offer) {
          state.data.offer = { ...state.data?.offer, ...offer };
        }
      }),
    );
  },
  setHeader: (livePrice) => {
    set(
      produce<State>((state) => {
        state.data = { ...state.data, ...livePrice };
      }),
    );
  },
  setIepIev: (payload) => {
    set(
      produce<State>((state) => {
        state.data.iepiev = payload
          ? {
              ...state.data?.iepiev,
              iep: {
                ...state.data?.iepiev?.iep,
                raw: payload.iep,
              },
              iev: {
                ...state.data?.iepiev?.iev,
                raw: payload.iev / 100,
              },
            }
          : null;
      }),
    );
  },
  setBestBidOffer: (payload) => {
    const { bestBid, bestOffer } = payload;
    set(
      produce<State>((state) => {
        if (bestBid) {
          state.data.iepiev = {
            ...state.data?.iepiev,
            best_bid_offer: {
              ...state.data?.iepiev?.best_bid_offer,
              bid: bestBid,
            },
          };
        }

        if (bestOffer) {
          state.data.iepiev = {
            ...state.data?.iepiev,
            best_bid_offer: {
              ...state.data?.iepiev?.best_bid_offer,
              offer: bestOffer,
            },
          };
        }
      }),
    );
  },
}));

export default useOrderbookCompanies;
